// Frontend telemetry — Azure Application Insights JS. Reports to the SAME
// App Insights resource the API uses (one dashboard, no new vendor — the
// Plan-1F deferred prerequisite). No-ops entirely when the connection string
// env var is absent (dev builds, un-configured environments) and must NEVER
// be able to break the app: every path is guarded.
import { ApplicationInsights } from '@microsoft/applicationinsights-web'

export interface Telemetry {
  trackException(error: unknown, properties?: Record<string, string>): void
  // A named, non-error signal (e.g. a 404 hit). Kept distinct from
  // trackException so it lands in App Insights `customEvents`, not
  // `exceptions` — 404s aren't crashes and shouldn't pollute exception counts
  // or exception-based alerts.
  trackEvent(name: string, properties?: Record<string, string>): void
  setAuthenticatedUser(userId: number): void
  clearAuthenticatedUser(): void
}

const noop: Telemetry = {
  trackException: () => {},
  trackEvent: () => {},
  setAuthenticatedUser: () => {},
  clearAuthenticatedUser: () => {},
}

const connectionString = import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING as
  | string
  | undefined

function init(): { telemetry: Telemetry; enabled: boolean } {
  if (!connectionString) return { telemetry: noop, enabled: false }
  try {
    const ai = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true, // SPA route changes as page views
        disableFetchTracking: false, // API dependency tracking
        // Explicitly enabled — the SDK default is false, NOT on-by-default
        // as the spec originally assumed.
        enableUnhandledPromiseRejectionTracking: true,
        // sampling stays 100% — CSFBL volume doesn't warrant less (spec)
      },
    })
    ai.loadAppInsights()
    // Track the initial page load explicitly: enableAutoRouteTracking only
    // fires on subsequent SPA route changes, never the first view.
    ai.trackPageView()
    return {
      enabled: true,
      telemetry: {
        trackException: (error, properties) => {
          try {
            ai.trackException({
              exception: error instanceof Error ? error : new Error(String(error)),
              properties,
            })
          } catch {
            /* telemetry must never throw */
          }
        },
        trackEvent: (name, properties) => {
          try {
            ai.trackEvent({ name }, properties)
          } catch {
            /* telemetry must never throw */
          }
        },
        setAuthenticatedUser: (userId) => {
          try {
            ai.setAuthenticatedUserContext(String(userId))
          } catch {
            /* never throw */
          }
        },
        clearAuthenticatedUser: () => {
          try {
            ai.clearAuthenticatedUserContext()
          } catch {
            /* never throw */
          }
        },
      },
    }
  } catch {
    return { telemetry: noop, enabled: false }
  }
}

const initialized = init()
export const telemetry: Telemetry = initialized.telemetry
export const isTelemetryEnabled: boolean = initialized.enabled
